import { useState, useEffect } from "react";
import { admin } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Plus, Trash2, X, TestTube, Play, Mail, CheckCircle,
  AlertTriangle, XCircle, Eye, EyeOff, RefreshCw,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────

interface SeedAccount {
  id: string;
  provider: string;
  email: string;
  display_name: string | null;
  imap_host: string;
  imap_port: number;
  imap_tls: boolean;
  imap_user: string;
  imap_password: string;
  enabled: boolean;
  last_checked: string | null;
}

interface SeedResult {
  id: string;
  seed_email: string;
  provider: string;
  from_email: string;
  subject: string;
  placement: "inbox" | "spam" | "missing" | "error";
  sent_at: string;
  checked_at: string | null;
}

interface PlacementSummary {
  total: number;
  inbox: number;
  spam: number;
  missing: number;
  byProvider: Array<{ provider: string; total: number; inbox: number; spam: number; missing: number }>;
}

interface ProviderInfo {
  id: string;
  name: string;
  imap_host: string;
  imap_port: number;
}

const PLACEMENT_STYLES: Record<string, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
  inbox:   { icon: CheckCircle,    color: "text-green-600",  bg: "bg-green-50",  label: "Inbox" },
  spam:    { icon: AlertTriangle,  color: "text-red-600",    bg: "bg-red-50",    label: "Spam" },
  missing: { icon: XCircle,        color: "text-slate",      bg: "bg-gray-50",   label: "Missing" },
  error:   { icon: XCircle,        color: "text-amber-600",  bg: "bg-amber-50",  label: "Error" },
};

// ── Main Page ────────────────────────────────────────────────

export default function AdminSeedPage() {
  const [accounts, setAccounts]   = useState<SeedAccount[]>([]);
  const [results, setResults]     = useState<SeedResult[]>([]);
  const [summary, setSummary]     = useState<PlacementSummary | null>(null);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // Modal state
  const [showAdd, setShowAdd]         = useState(false);
  const [editing, setEditing]         = useState<SeedAccount | null>(null);
  const [deleting, setDeleting]       = useState<string | null>(null);
  const [testing, setTesting]         = useState<string | null>(null);
  const [testResult, setTestResult]   = useState<{ id: string; ok: boolean; error?: string } | null>(null);
  const [running, setRunning]         = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [accts, res, sum, prov] = await Promise.all([
        admin.seed.list(),
        admin.seed.results(7),
        admin.seed.summary(30),
        admin.seed.providers(),
      ]);
      setAccounts(accts.accounts);
      setResults(res.results);
      setSummary(sum.summary);
      setProviders(prov.providers);
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this seed account? All test results will be removed.")) return;
    setDeleting(id);
    try { await admin.seed.delete(id); load(); }
    catch (e: any) { alert(e.message); }
    finally { setDeleting(null); }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    setTestResult(null);
    try {
      const res = await admin.seed.test(id);
      setTestResult({ id, ...res });
    } catch (e: any) {
      setTestResult({ id, ok: false, error: e.message });
    } finally {
      setTesting(null);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await admin.seed.run();
      alert("Seed test cycle started — results will appear in a few minutes.");
      setTimeout(load, 180_000); // refresh after 3 min
    } catch (e: any) { alert(e.message); }
    finally { setRunning(false); }
  };

  if (loading) return <Spinner className="h-screen" />;

  const inboxRate = summary && summary.total > 0
    ? Math.round((summary.inbox / summary.total) * 100)
    : null;

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Inbox Placement Testing</h1>
          <p className="text-sm text-slate">Monitor where your emails land across providers</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRunNow} disabled={running || accounts.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold hover:border-black/20 disabled:opacity-40">
            {running ? <RefreshCw size={15} className="animate-spin" /> : <Play size={15} />}
            {running ? "Running…" : "Test Now"}
          </button>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
            <Plus size={15} /> Add Account
          </button>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        )}

        {/* Summary cards */}
        {summary && summary.total > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5 text-center">
              <p className={`text-4xl font-black ${inboxRate! >= 90 ? "text-green-600" : inboxRate! >= 70 ? "text-amber-600" : "text-red-600"}`}>
                {inboxRate}%
              </p>
              <p className="text-xs text-slate mt-1">Inbox Rate (30d)</p>
            </div>
            {[
              { label: "Inbox", value: summary.inbox, color: "text-green-600" },
              { label: "Spam",  value: summary.spam,  color: "text-red-600" },
              { label: "Missing", value: summary.missing, color: "text-slate" },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] p-5 text-center">
                <p className={`text-3xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-slate mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Per-provider breakdown */}
        {summary && summary.byProvider.length > 0 && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-slate mb-3">By Provider (30d)</p>
            <div className="space-y-2">
              {summary.byProvider.map((p) => {
                const pctInbox = p.total > 0 ? Math.round((p.inbox / p.total) * 100) : 0;
                return (
                  <div key={p.provider} className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-ink w-20 capitalize">{p.provider}</span>
                    <div className="flex-1 h-3 bg-black/[0.05] rounded-full overflow-hidden flex">
                      {p.inbox > 0 && <div className="h-full bg-green-500" style={{ width: `${(p.inbox / p.total) * 100}%` }} />}
                      {p.spam > 0  && <div className="h-full bg-red-500" style={{ width: `${(p.spam / p.total) * 100}%` }} />}
                      {p.missing > 0 && <div className="h-full bg-gray-300" style={{ width: `${(p.missing / p.total) * 100}%` }} />}
                    </div>
                    <span className={`text-xs font-bold w-12 text-right ${pctInbox >= 90 ? "text-green-600" : pctInbox >= 70 ? "text-amber-600" : "text-red-600"}`}>
                      {pctInbox}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Per-sender breakdown */}
        {summary && summary.bySender && summary.bySender.length > 0 && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-slate mb-3">By Sender (30d)</p>
            <div className="space-y-2">
              {summary.bySender.map((s: any) => {
                const pctInbox = s.total > 0 ? Math.round((s.inbox / s.total) * 100) : 0;
                return (
                  <div key={s.from_email} className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-ink w-48 truncate" title={s.from_email}>{s.from_email}</span>
                    <div className="flex-1 h-3 bg-black/[0.05] rounded-full overflow-hidden flex">
                      {s.inbox > 0 && <div className="h-full bg-green-500" style={{ width: `${(s.inbox / s.total) * 100}%` }} />}
                      {s.spam > 0  && <div className="h-full bg-red-500" style={{ width: `${(s.spam / s.total) * 100}%` }} />}
                      {s.missing > 0 && <div className="h-full bg-gray-300" style={{ width: `${(s.missing / s.total) * 100}%` }} />}
                    </div>
                    <span className={`text-xs font-bold w-12 text-right ${pctInbox >= 90 ? "text-green-600" : pctInbox >= 70 ? "text-amber-600" : "text-red-600"}`}>
                      {pctInbox}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Seed accounts */}
        <div>
          <h2 className="font-bold text-ink mb-3 flex items-center gap-2">
            <Mail size={16} className="text-teal" /> Seed Accounts
          </h2>
          {accounts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-12 text-center">
              <Mail size={32} className="mx-auto mb-3 text-slate opacity-30" />
              <p className="font-semibold text-ink mb-1">No seed accounts yet</p>
              <p className="text-sm text-slate mb-4">
                Add Gmail, Outlook, or Yahoo accounts to test where your emails land.
              </p>
              <button onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark">
                <Plus size={15} /> Add Account
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map((acc) => (
                <div key={acc.id} className={`bg-white rounded-xl border border-black/[0.06] p-4 flex items-center gap-4 ${!acc.enabled ? "opacity-50" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink text-sm">{acc.email}</span>
                      <span className="px-2 py-0.5 rounded-full bg-black/[0.04] text-[10px] font-bold uppercase text-slate">{acc.provider}</span>
                      {!acc.enabled && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-[10px] font-bold text-amber-600">DISABLED</span>}
                    </div>
                    <p className="text-xs text-slate mt-0.5">
                      {acc.imap_host}:{acc.imap_port}
                      {acc.last_checked && ` · Last tested ${new Date(acc.last_checked).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {testResult?.id === acc.id && (
                      <span className={`text-xs font-semibold mr-2 ${testResult.ok ? "text-green-600" : "text-red-500"}`}>
                        {testResult.ok ? "Connected" : testResult.error?.slice(0, 30)}
                      </span>
                    )}
                    <button onClick={() => handleTest(acc.id)} disabled={testing === acc.id} title="Test IMAP"
                      className="p-2 rounded-lg text-slate hover:text-teal hover:bg-teal/[0.06] disabled:opacity-40">
                      {testing === acc.id ? <RefreshCw size={14} className="animate-spin" /> : <TestTube size={14} />}
                    </button>
                    <button onClick={() => setEditing(acc)} title="Edit"
                      className="p-2 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04]">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => handleDelete(acc.id)} disabled={deleting === acc.id} title="Delete"
                      className="p-2 rounded-lg text-slate hover:text-red-500 hover:bg-red-50 disabled:opacity-40">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent results */}
        {results.length > 0 && (
          <div>
            <h2 className="font-bold text-ink mb-3">Recent Results (7d)</h2>
            <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/[0.06] text-xs text-slate uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Seed Account</th>
                    <th className="text-left px-4 py-3">Provider</th>
                    <th className="text-left px-4 py-3">Placement</th>
                    <th className="text-left px-4 py-3">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => {
                    const style = PLACEMENT_STYLES[r.placement] || PLACEMENT_STYLES.error;
                    const Icon = style.icon;
                    return (
                      <tr key={r.id} className="border-b border-black/[0.04] last:border-0">
                        <td className="px-4 py-3 font-medium text-ink">{r.seed_email}</td>
                        <td className="px-4 py-3 capitalize text-slate">{r.provider}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${style.color} ${style.bg}`}>
                            <Icon size={11} /> {style.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate">{new Date(r.sent_at).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAdd || editing) && (
        <SeedAccountModal
          account={editing}
          providers={providers}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Add/Edit Modal ───────────────────────────────────────────

function SeedAccountModal({
  account, providers, onClose, onSaved,
}: {
  account: SeedAccount | null;
  providers: ProviderInfo[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!account;
  const [provider, setProvider]       = useState(account?.provider || "gmail");
  const [email, setEmail]             = useState(account?.email || "");
  const [displayName, setDisplayName] = useState(account?.display_name || "");
  const [imapHost, setImapHost]       = useState(account?.imap_host || "imap.gmail.com");
  const [imapPort, setImapPort]       = useState(account?.imap_port || 993);
  const [imapTls, setImapTls]         = useState(account?.imap_tls ?? true);
  const [imapUser, setImapUser]       = useState(account?.imap_user || "");
  const [imapPassword, setImapPassword] = useState("");
  const [showPass, setShowPass]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Auto-fill IMAP defaults when provider changes
  const handleProviderChange = (p: string) => {
    setProvider(p);
    const info = providers.find((pr) => pr.id === p);
    if (info && info.imap_host) {
      setImapHost(info.imap_host);
      setImapPort(info.imap_port);
    }
  };

  const handleSave = async () => {
    if (!email || !imapHost || !imapUser) {
      setError("Email, IMAP host, and IMAP user are required");
      return;
    }
    if (!isEdit && !imapPassword) {
      setError("IMAP password is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data: any = {
        provider, email, display_name: displayName || undefined,
        imap_host: imapHost, imap_port: imapPort, imap_tls: imapTls,
        imap_user: imapUser,
      };
      if (imapPassword) data.imap_password = imapPassword;

      if (isEdit) {
        await admin.seed.update(account!.id, data);
      } else {
        data.imap_password = imapPassword;
        await admin.seed.create(data);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate hover:text-ink"><X size={20} /></button>
        <h2 className="text-lg font-bold mb-4">{isEdit ? "Edit" : "Add"} Seed Account</h2>

        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">{error}</div>}

        <div className="space-y-3">
          {/* Provider */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Provider</label>
            <select value={provider} onChange={(e) => handleProviderChange(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper">
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="test@gmail.com"
              className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper" />
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Display Name (optional)</label>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Gmail Test"
              className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper" />
          </div>

          {/* IMAP Host / Port */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">IMAP Host</label>
              <input type="text" value={imapHost} onChange={(e) => setImapHost(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Port</label>
              <input type="number" value={imapPort} onChange={(e) => setImapPort(parseInt(e.target.value) || 993)}
                className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper" />
            </div>
          </div>

          {/* IMAP User */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">IMAP Username</label>
            <input type="text" value={imapUser} onChange={(e) => setImapUser(e.target.value)} placeholder="test@gmail.com"
              className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] text-sm bg-paper" />
          </div>

          {/* IMAP Password */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">
              IMAP Password {isEdit && "(leave blank to keep current)"}
            </label>
            <div className="relative">
              <input type={showPass ? "text" : "password"} value={imapPassword} onChange={(e) => setImapPassword(e.target.value)}
                placeholder={isEdit ? "••••••••" : "App password"}
                className="w-full px-4 py-2.5 pr-10 rounded-lg border border-black/[0.08] text-sm bg-paper" />
              <button onClick={() => setShowPass(!showPass)} type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {provider === "gmail" && (
              <p className="text-[11px] text-slate mt-1">Use an App Password — go to Google Account → Security → App Passwords</p>
            )}
          </div>

          {/* TLS */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={imapTls} onChange={(e) => setImapTls(e.target.checked)}
              className="rounded border-gray-300" />
            Use TLS
          </label>
        </div>

        <div className="flex gap-3 justify-end mt-5">
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.08] hover:border-black/20">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Account"}
          </button>
        </div>
      </div>
    </div>
  );
}
